import { CarePlanState } from '@seen/core/models/care-plans/constants'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { activateCarePlan } from '@seen/core/models/care-plans/transactions'
import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { isOfType } from '@seen/core/utils'
import { Button } from '@seen/ui'
import { IconCheck, IconSignature, IconX } from '@tabler/icons-react'
import { type JSX, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useCarePlanData } from '#src/components/care-plan/useCarePlanData.ts'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface ActivateCarePlanButtonProps {
  carePlan: InitialCarePlan
  signatures: CarePlanSignatureTask[]
}

export function ActivateCarePlanButton({
  carePlan,
  signatures,
}: ActivateCarePlanButtonProps): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const { isPending: isActivating, mutate: mutateTransaction } =
    useTransactionMutation()
  const {
    carePlanParticipantSignatures,
    goalCarePlans,
    interventions,
    demographics,
  } = useCarePlanData(carePlan.patientSubject, carePlan.reference)
  const navigate = useNavigate()
  const { data: participantCarePlans } = useSearch(
    InitialCarePlan,
    InitialCarePlan.byParticipant(carePlan.patientSubject)
  )
  const currentActiveCarePlans = participantCarePlans?.filter(
    (cp) => cp.state === CarePlanState.Active
  )
  const executeActivateCarePlan = useCallback(() => {
    mutateTransaction(
      (transaction) =>
        activateCarePlan(transaction, {
          carePlan,
          goalCarePlans,
          interventions,
          author: practitioner.reference,
          signatures,
          participantSignatures: carePlanParticipantSignatures,
          demographics,
          currentActiveCarePlans: currentActiveCarePlans ?? [],
        }),
      {
        onSuccess: (result) => {
          const carePlanSnapshot = result.entry?.find((e) =>
            isOfType(e.resource, 'Composition')
          )
          if (carePlanSnapshot) {
            void navigate(`../../history/${carePlanSnapshot.resource?.id}`)
          }
        },
      }
    )
  }, [
    mutateTransaction,
    carePlan,
    goalCarePlans,
    interventions,
    practitioner.reference,
    signatures,
    carePlanParticipantSignatures,
    demographics,
    navigate,
    currentActiveCarePlans,
  ])

  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()

  const handleActivateCarePlan = useCallback(() => {
    confirmWithAnnotation({
      title: 'Activate Care Plan',
      description: `Are you sure you want to activate the care plan? This will create a signed version of the care plan and move the current care plan into Active state`,
      onConfirm: () => {
        executeActivateCarePlan()
      },
    })
  }, [confirmWithAnnotation, executeActivateCarePlan])

  const idtSignaturesCompleted =
    signatures.length > 0 && signatures.every((signature) => signature.isDone)
  const participantSignatureCompleted = carePlanParticipantSignatures.some(
    (signature) => signature.isDone
  )

  const isActivated = carePlan.state === CarePlanState.Active
  // Activation is gated on signatures + review being complete, not on the plan
  // having been moved to PendingSignatures. Signatures can be created
  // out-of-band (e.g. by scripts) which leaves the plan in Draft; allow
  // activating from either pre-active state once everything is signed.
  const canActivateFromState =
    carePlan.state === CarePlanState.PendingSignatures ||
    carePlan.state === CarePlanState.Draft

  return (
    <div className="flex flex-row items-center gap-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              onClick={handleActivateCarePlan}
              variant="default"
              disabled={
                !(
                  idtSignaturesCompleted &&
                  participantSignatureCompleted &&
                  canActivateFromState
                )
              }
              loading={isActivating}
            >
              <IconSignature className="mr-2 size-4" /> Activate Care Plan
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="w-80 p-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {canActivateFromState ? (
                <IconCheck className="size-4 text-green-500" />
              ) : (
                <IconX className="size-4 text-red-500" />
              )}
              <span>
                {carePlan.state === CarePlanState.Active
                  ? 'Care plan is already active'
                  : canActivateFromState
                    ? `Status: ${carePlan.state}`
                    : `Status: ${carePlan.state} (cannot activate)`}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {idtSignaturesCompleted ? (
                <IconCheck className="size-4 text-green-500" />
              ) : (
                <IconX className="size-4 text-red-500" />
              )}
              <span>
                {CarePlanSignatureTask.completedRatio(signatures)} IDT
                signatures collected
              </span>
            </div>
            <div className="flex items-center gap-1">
              {participantSignatureCompleted ? (
                <IconCheck className="size-4 text-green-500" />
              ) : (
                <IconX className="size-4 text-red-500" />
              )}
              <span>Participant signature collected</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
      {isActivated && carePlan.startDate && (
        <span className="text-sm text-foreground-secondary">
          Activated on{' '}
          <DateTimeDisplay
            value={carePlan.startDate}
            format="MM/dd/yy"
            element="span"
          />
        </span>
      )}
    </div>
  )
}
